<template>
  <div class="flex flex-col h-full">
    <!-- Injector Details Header Section -->
    <div
      class="bg-surface-0 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700 p-4 mb-4"
      data-test-id="records-header"
    >
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3
            class="text-lg font-semibold text-surface-900 dark:text-surface-0"
          >
            Tax Injector Records
          </h3>
          <p class="text-sm text-surface-600 dark:text-surface-300 mt-1">
            View transaction records for this injection
          </p>
        </div>
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          variant="outlined"
          size="small"
          @click="handleRefresh"
          :loading="loading"
          data-test-id="records-refresh-btn"
        />
      </div>

      <!-- Injector Details -->
      <div
        v-if="injector"
        class="border-t border-surface-200 dark:border-surface-700 pt-4"
      >
        <dl class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div class="flex flex-col gap-1">
            <dt class="text-surface-500 dark:text-surface-400 font-medium">
              Injector ID
            </dt>
            <dd class="flex items-center gap-2">
              <span
                class="font-mono text-surface-900 dark:text-surface-0 text-xs break-all"
              >
                {{ injector.ExternalId }}
              </span>
              <Button
                icon="pi pi-copy"
                variant="text"
                size="small"
                severity="secondary"
                @click="copyToClipboard(injector.ExternalId)"
                v-tooltip.top="'Copy ID'"
                class="p-0 w-6 h-6"
                data-test-id="copy-injector-id-btn"
              />
            </dd>
          </div>
          <div class="flex flex-col gap-1">
            <dt class="text-surface-500 dark:text-surface-400 font-medium">
              Created At
            </dt>
            <dd class="text-surface-900 dark:text-surface-0">
              <AuditInfo
                :audit="injectorAuditInfo"
                :showModified="false"
                data-test-id="injector-audit-info"
              />
            </dd>
          </div>
        </dl>

        <!-- File Download Section -->
        <div
          v-if="injector && injector.Filename"
          class="mt-4 pt-4 border-t border-surface-200 dark:border-surface-700"
        >
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-1">
              <dt
                class="text-surface-500 dark:text-surface-400 font-medium text-sm"
              >
                Original File
              </dt>
              <dd class="text-surface-900 dark:text-surface-0 text-sm">
                Click to download the original CSV file that was uploaded for
                this tax injector.
              </dd>
            </div>
            <Button
              label="Download File"
              icon="pi pi-download"
              variant="outlined"
              size="small"
              severity="primary"
              @click="onDownloadFile"
              :loading="isDownloading"
              :disabled="!injector.Filename"
              data-test-id="download-original-file-btn"
            />
          </div>
        </div>

        <!-- No File Available Section -->
        <div
          v-else-if="injector && !injector.Filename"
          class="mt-4 pt-4 border-t border-surface-200 dark:border-surface-700"
        >
          <div
            class="flex items-center gap-2 text-surface-500 dark:text-surface-400 text-sm"
          >
            <i class="pi pi-info-circle"></i>
            <span>No original file available for download</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Records Table -->
    <div
      class="flex-1 bg-surface-0 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700"
    >
      <DataTable
        :value="loading && records.length === 0 ? skeletonData : records"
        :paginator="records.length > 0"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50]"
        class="p-datatable-sm"
        stripedRows
        responsiveLayout="scroll"
        scrollable
        scrollHeight="calc(100vh - 26rem)"
        sortField="CreatedAt"
        :sortOrder="-1"
        :loading="loading && records.length > 0"
        data-test-id="tax-injector-records-table"
      >
        <!-- Empty state -->
        <template #empty>
          <div
            class="flex flex-col items-center justify-center py-12 text-surface-500 dark:text-surface-400"
            data-test-id="records-table-empty"
          >
            <i class="pi pi-inbox text-4xl mb-3"></i>
            <p class="text-lg font-medium">No records found</p>
            <p class="text-sm">This tax injector has no transaction records.</p>
          </div>
        </template>

        <!-- Transaction ID Column -->
        <Column
          field="TransactionExternalId"
          header="Transaction ID"
          :sortable="true"
          style="min-width: 20rem"
        >
          <template #body="{ data }">
            <div v-if="loading && records.length === 0">
              <Skeleton width="16rem" height="1rem"></Skeleton>
            </div>
            <div
              v-else-if="data.TransactionExternalId"
              class="flex items-center gap-2"
            >
              <button
                @click="openTransactionSearch(data)"
                :disabled="!data.MerchantId"
                class="font-mono text-sm text-rch-black dark:text-surface-300 hover:text-surface-800 dark:hover:text-surface-200 underline underline-offset-4 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                data-test-id="transaction-link"
              >
                {{ data.TransactionExternalId }}
              </button>
              <Button
                icon="pi pi-copy"
                variant="text"
                size="small"
                severity="secondary"
                @click.stop="copyToClipboard(data.TransactionExternalId)"
                v-tooltip.top="'Copy Transaction ID'"
                class="p-0 w-6 h-6"
                data-test-id="copy-transaction-id-btn"
              />
            </div>
            <span v-else class="text-sm text-surface-400 dark:text-surface-500"
              >—</span
            >
          </template>
        </Column>

        <!-- Reference Column -->
        <Column
          field="Reference"
          header="Reference"
          :sortable="true"
          style="min-width: 10rem"
        >
          <template #body="{ data }">
            <div v-if="loading && records.length === 0">
              <Skeleton width="8rem" height="1rem"></Skeleton>
            </div>
            <span v-else class="text-sm text-surface-700 dark:text-surface-200">
              {{ data.Reference }}
            </span>
          </template>
        </Column>

        <!-- Status Column -->
        <Column
          field="Status"
          header="Status"
          :sortable="true"
          style="min-width: 10rem"
        >
          <template #body="{ data }">
            <div v-if="loading && records.length === 0">
              <Skeleton width="6rem" height="1rem"></Skeleton>
            </div>
            <BadgeInjectorRecordStatus v-else :status="data.Status" />
          </template>
        </Column>

        <!-- Created At Column -->
        <Column
          field="CreatedAt"
          header="Created At"
          :sortable="true"
          style="min-width: 12rem"
        >
          <template #body="{ data }">
            <div v-if="loading && records.length === 0">
              <Skeleton width="10rem" height="1rem"></Skeleton>
            </div>
            <span v-else class="text-sm text-surface-700 dark:text-surface-200">
              {{ formatDateTimeForDisplay(data.CreatedAt) }}
            </span>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Skeleton from 'primevue/skeleton'
import Button from 'primevue/button'
import BadgeInjectorRecordStatus from '@/components/taxinjector/BadgeInjectorRecordStatus.vue'
import AuditInfo from '@/components/common/AuditInfo.vue'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import { successToast } from '@/util/toasts'
import type { TaxInjectorRecord, TaxInjectorSummary } from '@/types/taxInjector'
import type { AuditRecord } from '@/types/audit'

interface Props {
  records: TaxInjectorRecord[]
  loading: boolean
  externalId: string
  injector?: TaxInjectorSummary | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  refresh: []
  'download-file': [injector: TaxInjectorSummary]
}>()

const toast = useToast()
const router = useRouter()

// Local state for download functionality
const isDownloading = ref(false)

// Skeleton data for loading state
const skeletonData = computed(() => Array(5).fill({}))

// Map injector data to AuditRecord format for AuditInfo component
const injectorAuditInfo = computed<AuditRecord | null>(() => {
  if (!props.injector) return null
  return {
    CreatedOn: props.injector.CreatedAt,
    CreatedBy: props.injector.CreatedBy,
  }
})

function handleRefresh() {
  emit('refresh')
}

function onDownloadFile() {
  if (props.injector && props.injector.Filename) {
    isDownloading.value = true
    emit('download-file', props.injector)
  }
}

// Function to clear downloading state (called from parent after download completes)
function clearDownloadingState() {
  isDownloading.value = false
}

// Expose the function so parent can call it
defineExpose({
  clearDownloadingState,
})

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    successToast(toast, 'Copied', 'ID copied to clipboard')
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    successToast(toast, 'Copied', 'ID copied to clipboard')
  }
}

function openTransactionSearch(record: TaxInjectorRecord) {
  if (!record.TransactionExternalId) {
    console.warn('openTransactionSearch called without TransactionExternalId')
    return
  }

  if (!record.MerchantId) {
    console.warn('openTransactionSearch called without MerchantId')
    return
  }

  // Navigate directly to the merchant's transaction detail page
  router.push({
    name: 'merchantTransactionSearchDetail',
    params: {
      id: record.MerchantId,
      transactionId: record.TransactionExternalId,
    },
  })
}
</script>
